import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, InputAdornment, Icon } from '@mui/material';
import MDButton from 'components/MDButton';
import AngleRightIcon from "common/customIcons/angle_right";
import SearchIcon from "common/customIcons/search";
import { getBoxDetails } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import CommonLayout from "layouts/DashboardLayout";
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import { SubCategoryFilter } from "common/filters";
import { BrandFilter } from "common/filters";
import { debounce } from "utilities/common";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";

const initialValues = {
  name: "",
  description: "",
  isActive: true,
}

const BoxDetails = () => {
  const { boxId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [details, setDetails] = useState({ ...initialValues })
  const [refreshTable, setRefreshTable] = useState(false);
  const [customFilters, setCustomFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const columnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'adminProductName',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 300,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { adminProductName, sku, assets } = params?.data || {};
        const { thumbURL, name } = assets?.[0] || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          <img src={thumbURL} alt={name} width={30} height={30} />
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{adminProductName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Category',
      field: 'category',
      suppressMenu: false,
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueGetter: ({ data }) => data?.category?.name || "-",
    },
    {
      headerName: 'Brand',
      field: 'brand',
      suppressMenu: false,
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueGetter: ({ data }) => data?.brand?.name || "-",
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      suppressMenu: false,
      sortable: false,
      flex: 1,
      minWidth: 150,
      headerClass: 'jc-start',
    },
  ];

  const handleBoxDetails = async () => {
    try {
      const response = await dispatch(getBoxDetails({ boxId })).unwrap()
      if (response?.data) {
        setDetails({ ...response.data })
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  }

  const handleFilterChange = async (e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleSearch = useCallback(debounce((event) => {
    setCustomFilters(prev => ({ ...prev, search: event.target.value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  useEffect(() => {
    if (boxId) {
      handleBoxDetails();
    }
  }, [boxId])

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Box
            </Typography>
          </Box>
          <Box style={{ display: "flex", gap: "10px" }}>
            {/* <MDButton
              style={{ minWidth: "140px" }}
              startIcon={<AddRoundedIcon fontSize="small" />}
              variant="contained"
              color="primary"
              size="small"
              onClick={() => navigate(`/hyperwolf/boxes?tab=products&box=${boxId}`)}
            >
              Assign Products
            </MDButton> */}
            {
              canEdit &&
              <MDButton
                type="button"
                variant="outlined"
                color="dark"
                size="small"
                style={{ minWidth: "120px" }}
                onClick={() => navigate(`/hyperwolf/boxes/update-box/${boxId}`)}
              >
                <Icon>edit</Icon>&nbsp;edit
              </MDButton>
            }
          </Box>
        </Stack>
        {
          canView &&
          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container spacing={2}>

              <>
                {/* Basic details */}
                <Grid item xs={12}>
                  <Card sx={{ p: 0, borderRadius: 2.5, boxShadow: "none" }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Box Name</Typography>
                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{details?.name}</Typography>
                      </Grid>
                      {details.description && <Grid item xs={12}>
                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Description</Typography>
                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{details.description}</Typography>
                      </Grid>}
                    </Grid>
                    <Box pt={{ md: 2 }}>
                      <hr className='my-3' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight="semibold" mb={2}>Box Products</Typography>

                  {/* Filters */}
                  <Box className='cus-filter-sec filter-gray-bg' width={"100%"}>
                    <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                      <Grid item xs={12} lg={3} xxxl={2} className='p-remove'>
                        <SubCategoryFilter
                          value={customFilters?.categoryId}
                          handleChange={handleFilterChange}
                          placeholder={"categories"}
                          selectAllLabel={"All Categories"}
                        />
                      </Grid>
                      <Grid item xs={12} lg={3} xxxl={2}>
                        <BrandFilter
                          value={customFilters?.brandId}
                          handleChange={handleFilterChange}
                          placeholder={"Brands"}
                          selectAllLabel={"All"}
                        />
                      </Grid>
                      <Grid item xs={12} lg={3} xxxl={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ sm: "center" }}
                          justifyContent="end"
                          className="filter-search"
                        >
                          <Box className="search-gray">
                            <TextField
                              className="custom-search-field"
                              placeholder="Search"
                              value={searchTerm || ""}
                              onChange={e => {
                                const { value } = e.target;
                                setSearchTerm(value);
                                handleSearch(e);
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">
                                  <IconButton size='small'>
                                    <SearchIcon width='18px' height='18px' />
                                  </IconButton>
                                </InputAdornment>
                              }}
                            />
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Products listing */}
                <Grid item xs={12} className="table-border-gray last-child-left">
                  <Card sx={{ p: 0, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
                    <div className="ag-theme-quartz" style={{ height: "50vh", width: "100%" }}>
                      <CommonAgGridTable
                        tableName="products"
                        pagination={false}
                        columnDefs={columnDefs}
                        getDataMethod={getBoxDetails}
                        refreshTable={refreshTable}
                        customFilters={{ ...customFilters, boxId }}
                      />
                    </div>
                  </Card>
                </Grid>
              </>
            </Grid>
          </Card>
        }

      </Box>
    </CommonLayout >
  )
}

export default BoxDetails
